'use client'

import { AIRI, BannerShop, IconCreditFlashOrange } from "@/assets";
import { Buttons, Images } from "@/components";
import AvatarPlaceholder from "@/components/avatar-default";
import ClientOnly from "@/components/client-only";
import useDecorByUser from "@/hooks/useDecorByUser";
import { useAuth } from "@/provider/UserProvider";
import { formatCurrency } from "@/utils";
import { useDisclosure } from "@nextui-org/react";
import Image from "next/image";
import { useState } from "react";
import { toast } from "react-toastify";
import PreviewDecor from "./Preview";
import SkeletonListDecor from "./Skeleton";
import useFetch, { AvatarDecor } from "./useFetch";

const Shop = () => {
  const { auth, isLoginClient } = useAuth();
  const [itemActive, setItemActive] = useState<AvatarDecor>();
  const { isOpen, onOpen, onOpenChange } = useDisclosure();
  const { list } = useFetch();
  const { isBoughtDecor, refresh } = useDecorByUser();

  const handleOpen = (item: AvatarDecor) => {
    if (!isLoginClient) return toast.warning('Please login to continue!')
    setItemActive(item)
    onOpen();
  }

  return (
    <div className="main-container max-w-[1400px]">
      <div className="-mx-4 sm:-mx-1 sm:my-4">
        <Image className="w-full" alt="banner" src={BannerShop} />
      </div>
      <ClientOnly skeleton={<SkeletonListDecor />}>
        <div className="grid grid-cols-5 lg:grid-cols-4 md:grid-cols-3 sm:grid-cols-2 gap-4 sm:gap-3 mb-10">
          {list.map(item => (
            <div key={item.id} onClick={() => handleOpen(item)} className="border-2 cursor-pointer group overflow-hidden border-gray-200 rounded-xl">
              <div className="relative bg-gray-100 overflow-hidden before:pb-[100%] before:block">
                <div className="absolute overflow-hidden flex items-center justify-center top-[50%] left-[50%] w-[53%] h-[53%] -translate-x-[50%] -translate-y-[50%] border-1 border-gray-200 rounded-full">
                  <Image aria-checked={isLoginClient} alt="airi" className="max-w-[50px] 2xl:max-w-[40px] aria-checked:group-hover:hidden max-h-full opacity-20" src={AIRI} />
                  {isLoginClient && (
                    <div className="hidden group-hover:block w-full h-full">
                      <AvatarPlaceholder 
                        address={auth.user.publicAddress}
                        src={auth.user.picture}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                </div>
                <Images alt="image" className="max-w-[85%] max-h-[85%] absolute top-[50%] left-[50%] -translate-x-[50%] -translate-y-[50%]" src={item.decoUrl} />
              </div>
              <div className="p-4 sm:p-3">
                <p className="font-semibold text-[15px] 2xl:text-[14px] sm:text-[13px] mb-1 line-clamp-1 capitalize">{item.name}</p>
                <div className="flex justify-between items-center">
                  <div className="flex items-center">
                    <p className="text-[15px] 2xl:text-[14px] sm:text-[13px] mr-[1px] sm:mr-0 text-orange-500 font-semibold">{formatCurrency(item.price)}</p>
                    <IconCreditFlashOrange className="sm:scale-80" />
                  </div>
                  {isBoughtDecor(item.id) ? (
                    <Buttons typeBtn="disabled" className="h-[30px] sm:h-[28px] sm:px-3 min-w-0">
                      <span className="font-medium text-[13px] sm:text-[12px]">Bought</span>
                    </Buttons>
                  ) : (
                    <Buttons onClick={() => handleOpen(item)} className="h-[30px] sm:h-[28px] sm:px-3 min-w-0">
                      <span className="font-medium text-[13px] sm:text-[12px]">Buy</span>
                    </Buttons>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </ClientOnly>
      <PreviewDecor 
        info={itemActive} 
        isOpen={isOpen} 
        onOpenChange={onOpenChange}
        isBoughtDecor={isBoughtDecor}
        refresh={refresh}
      />
    </div>
  );
}

export default Shop;